import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { TrendingUp, Trash2, Plus } from "lucide-react";

export default function PricingTab() {
  const [rules, setRules] = useState([]);
  const [weekend, setWeekend] = useState(0);
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "", percent: "" });
  const [saving, setSaving] = useState(false);
  const [roadsideEnabled, setRoadsideEnabled] = useState(true);
  const [roadsideDelay, setRoadsideDelay] = useState(24);
  const [hsEnabled, setHsEnabled] = useState(false);
  const [hsPct, setHsPct] = useState(15);

  const load = () => api.get("/pricing/rules").then((r) => { setRules(r.data.rules); setWeekend(r.data.weekend_premium_pct); setRoadsideEnabled(r.data.roadside_addon_enabled !== false); setRoadsideDelay(r.data.roadside_delay_hours ?? 24); setHsEnabled(!!r.data.high_season_enabled); setHsPct(r.data.high_season_pct ?? 15); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveRule = async () => {
    if (!form.start_date || !form.end_date || !form.percent) return toast.error("Dates and % are required");
    setSaving(true);
    try {
      await api.post("/admin/pricing-rules", { ...form, percent: Number(form.percent) });
      toast.success("Pricing rule created");
      setForm({ name: "", start_date: "", end_date: "", percent: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const saveRoadside = async () => {
    try {
      await api.patch("/admin/pricing-config", { weekend_premium_pct: Number(weekend) || 0, roadside_addon_enabled: roadsideEnabled, roadside_delay_hours: Number(roadsideDelay) || 0 });
      toast.success("Roadside add-on settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveWeekend = async () => {
    try {
      await api.patch("/admin/pricing-config", { weekend_premium_pct: Number(weekend) || 0 });
      toast.success("Weekend premium saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this pricing rule?")) return;
    try {
      await api.delete(`/admin/pricing-rules/${id}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-pricing-tab">
      <AdminHeader testid="admin-header-pricing" eyebrow="Revenue" title="Smart Pricing" description="Seasonal rate adjustments and weekend premiums — applied automatically to new bookings." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-6 h-fit">
          <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6">
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
              <TrendingUp size={14} className="text-[#C71E2E]" /> Weekend Premium
            </p>
            <div className="mt-5 space-y-4">
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Saturday & Sunday surcharge (%)</Label>
                <Input data-testid="pricing-weekend-input" type="number" min={0} max={100} value={weekend} onChange={(e) => setWeekend(e.target.value)} className="input-dark mt-1.5" />
                <p className="mt-1.5 text-[11px] text-muted-foreground">0 = no weekend premium. Applies per weekend day.</p>
              </div>
              <Button data-testid="pricing-weekend-save" onClick={saveWeekend} className="w-full rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui hover:scale-95 transition-transform">Save Weekend Premium</Button>
            </div>
          </div>
          <div data-testid="roadside-addon-card" className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6">
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
              <TrendingUp size={14} className="text-[#C71E2E]" /> Mid-Rental Roadside Add-On
            </p>
            <div className="mt-5 space-y-4">
              <label className="flex items-center gap-3 text-sm font-ui text-[#1A1A1A] cursor-pointer">
                <input data-testid="roadside-enabled-toggle" type="checkbox" checked={roadsideEnabled} onChange={(e) => setRoadsideEnabled(e.target.checked)} className="accent-[#C71E2E] w-4 h-4" />
                Allow renters to add roadside assistance mid-rental ($15/day, remaining days)
              </label>
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Activation delay (hours)</Label>
                <Input data-testid="roadside-delay-input" type="number" min={0} step={1} value={roadsideDelay} onChange={(e) => setRoadsideDelay(e.target.value)} className="input-dark mt-1.5" />
                <p className="mt-1.5 text-[11px] text-muted-foreground">Anti-fraud blackout — coverage activates this many hours after payment.</p>
              </div>
              <Button data-testid="roadside-settings-save" onClick={saveRoadside} className="w-full rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui hover:scale-95 transition-transform">Save Roadside Settings</Button>
            </div>
          </div>
          <div data-testid="high-season-card" className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6">
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
              <TrendingUp size={14} className="text-[#C71E2E]" /> High Season (Dec–Apr)
            </p>
            <div className="mt-5 space-y-4">
              <label className="flex items-center gap-3 text-sm font-ui text-[#1A1A1A] cursor-pointer">
                <input data-testid="high-season-toggle" type="checkbox" checked={hsEnabled} onChange={(e) => setHsEnabled(e.target.checked)} className="accent-[#C71E2E] w-4 h-4" />
                Apply high-season pricing automatically (December 1 – April 30)
              </label>
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Seasonal surcharge (%)</Label>
                <Input data-testid="high-season-pct-input" type="number" min={0} max={100} value={hsPct} onChange={(e) => setHsPct(e.target.value)} className="input-dark mt-1.5" />
                <p className="mt-1.5 text-[11px] text-muted-foreground">Applies per in-season day, stacks with weekend premium & seasonal rules. Shown to guests at checkout.</p>
              </div>
              <Button data-testid="high-season-save" onClick={async () => {
                try {
                  await api.patch("/admin/pricing-config", { weekend_premium_pct: Number(weekend) || 0, high_season_enabled: hsEnabled, high_season_pct: Number(hsPct) || 0 });
                  toast.success(hsEnabled ? `High season ON — +${Number(hsPct) || 0}% for Dec–Apr days` : "High season pricing off");
                } catch (e) { toast.error(errMsg(e)); }
              }} className="w-full rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui hover:scale-95 transition-transform">Save High Season</Button>
            </div>
          </div>
          <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6">
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
              <Plus size={14} className="text-[#C71E2E]" /> Seasonal Rule
            </p>
            <div className="mt-5 space-y-4">
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Name</Label>
                <Input data-testid="pricing-rule-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Christmas peak" className="input-dark mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-ui text-xs text-muted-foreground">From</Label>
                  <Input data-testid="pricing-rule-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="input-dark mt-1.5" />
                </div>
                <div>
                  <Label className="font-ui text-xs text-muted-foreground">To</Label>
                  <Input data-testid="pricing-rule-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="input-dark mt-1.5" />
                </div>
              </div>
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Rate adjustment (% — positive or negative)</Label>
                <Input data-testid="pricing-rule-percent" type="number" value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} placeholder="15 or -10" className="input-dark mt-1.5" />
              </div>
              <Button data-testid="pricing-rule-save" onClick={saveRule} disabled={saving} className="w-full rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui hover:scale-95 transition-transform">
                {saving ? "Creating…" : "Add Rule"}
              </Button>
            </div>
          </div>
        </div>
        <div className="lg:col-span-2 space-y-3 h-fit">
          {rules.length === 0 && <p data-testid="pricing-rules-empty" className="text-muted-foreground font-ui text-sm bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">No seasonal rules — base rates apply year-round.</p>}
          {rules.map((r) => (
            <div key={r.id} data-testid={`pricing-rule-${r.id}`} className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl px-6 py-4 flex items-center justify-between gap-4">
              <div>
                <p className="font-ui font-semibold text-[#1A1A1A]">{r.name}</p>
                <p className="text-xs text-muted-foreground font-ui">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className={`font-ui font-semibold ${r.percent >= 0 ? "text-[#C71E2E]" : "text-emerald-600"}`}>{r.percent >= 0 ? "+" : ""}{r.percent}%</span>
                <button data-testid={`pricing-rule-delete-${r.id}`} onClick={() => remove(r.id)} className="text-muted-foreground hover:text-red-700 transition-colors duration-200" aria-label="Delete rule">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
